import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Button, Progress, Modal, Spin } from 'antd';
import { 
  ThunderboltOutlined,
  WarningOutlined,
  BarChartOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  BookOutlined,
  CheckOutlined,
  RightOutlined,
  LoadingOutlined,
  ArrowUpOutlined,
  LockOutlined,
  FolderOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import axios from 'axios';
import styles from './Home.module.css'; // 导入CSS模块

const { Title, Text } = Typography;

// 创建API实例
const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  withCredentials: true
});

// 添加请求拦截器，自动添加认证令牌
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器，处理常见错误
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // 处理401未授权错误
      if (error.response.status === 401) {
        console.log('用户未登录或登录已过期');
        // 可以在这里添加重定向到登录页面的逻辑
        // window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 定义可能的学习页面类型
const STUDY_PAGE_TYPES = {
  KNOWLEDGE_CARD: 'knowledge_card',
  QUESTION_BANK: 'question_bank',
  WRONG_QUESTIONS: 'wrong_questions',
  EXAM_PRACTICE: 'exam_practice',  // 添加考试练习类型
};

// 本地存储键名
const LAST_STUDY_PAGE_KEY = 'last_study_page';
const USER_LEARNING_RECORDS_KEY = 'user_learning_records';
const DAILY_TASKS_KEY = 'daily_tasks';
const USER_PROFESSION_KEY = 'user_profession';
const LEARNING_GOALS_KEY = 'learning_goals';

// 预定义的专业及其默认学习目标
const PROFESSIONS = {
  TEACHER: {
    id: 'teacher',
    name: '教师资格',
    goals: {
      knowledgeCards: 30,
      examQuestions: 200,
      wrongQuestions: 20
    },
    categories: ['幼儿园', '小学', '中学']
  },
  ACCOUNTANT: {
    id: 'accountant',
    name: '会计资格',
    goals: {
      knowledgeCards: 40,
      examQuestions: 300,
      wrongQuestions: 30
    },
    categories: ['初级会计', '中级会计', '注册会计师']
  },
  CIVIL_SERVANT: {
    id: 'civil_servant',
    name: '公务员',
    goals: {
      knowledgeCards: 50,
      examQuestions: 400,
      wrongQuestions: 40
    },
    categories: ['行政职业能力', '申论', '面试']
  }
};

// 全局函数：记录用户访问的学习页面
// 将此函数定义在组件外部，使其可以在任何地方调用
const recordStudyPageVisit = (pageType, path, title, params = {}) => {
  // 保存完整的页面信息，包括所有参数
  const studyPageData = {
    type: pageType,
    path,
    title,
    timestamp: new Date().toISOString(),
    ...params
  };
  
  try {
    // 同时保存页面类型和路径的映射，用于后续直接访问时的参数恢复
    const pageTypeMap = JSON.parse(localStorage.getItem('page_type_map') || '{}');
    pageTypeMap[path] = { type: pageType, params };
    localStorage.setItem('page_type_map', JSON.stringify(pageTypeMap));
    
    localStorage.setItem(LAST_STUDY_PAGE_KEY, JSON.stringify(studyPageData));
    console.log('Saved study page:', studyPageData); // 调试日志
    
    // 记录学习记录到本地存储
    recordLearningActivity(pageType, path, params);
  } catch (error) {
    console.error('Error saving study page data:', error);
  }
};

// 记录学习活动到本地存储
const recordLearningActivity = (pageType, path, params = {}) => {
  try {
    // 获取现有记录
    const records = JSON.parse(localStorage.getItem(USER_LEARNING_RECORDS_KEY) || '[]');
    
    // 创建新记录
    const newRecord = {
      type: pageType,
      path,
      timestamp: new Date().toISOString(),
      params,
      completed: true // 默认标记为已完成
    };
    
    // 添加到记录列表
    records.push(newRecord);
    
    // 保存回本地存储
    localStorage.setItem(USER_LEARNING_RECORDS_KEY, JSON.stringify(records));
    
    // 更新今日任务完成状态
    updateDailyTasksCompletion(pageType, params);
    
    console.log('Recorded learning activity:', newRecord);
  } catch (error) {
    console.error('Error recording learning activity:', error);
  }
};

// 更新今日任务完成状态
const updateDailyTasksCompletion = (pageType, params = {}) => {
  try {
    // 获取今日任务
    const dailyTasks = JSON.parse(localStorage.getItem(DAILY_TASKS_KEY) || '[]');
    
    // 如果没有今日任务，生成默认任务
    if (dailyTasks.length === 0) {
      generateDefaultDailyTasks();
      return;
    }
    
    // 查找匹配的任务并标记为已完成
    let updated = false;
    for (const task of dailyTasks) {
      if (task.type === pageType) {
        // 根据不同类型的任务，检查参数是否匹配
        if (pageType === STUDY_PAGE_TYPES.KNOWLEDGE_CARD && params.code === task.code) {
          task.completed = true;
          updated = true;
        } else if (pageType === STUDY_PAGE_TYPES.EXAM_PRACTICE && 
                  params.examData === task.examData) {
          task.completed = true;
          updated = true;
        } else if (pageType === STUDY_PAGE_TYPES.WRONG_QUESTIONS) {
          task.completed = true;
          updated = true;
        } else if (pageType === STUDY_PAGE_TYPES.QUESTION_BANK) {
          task.completed = true;
          updated = true;
        }
      }
    }
    
    if (updated) {
      localStorage.setItem(DAILY_TASKS_KEY, JSON.stringify(dailyTasks));
      console.log('Updated daily tasks completion:', dailyTasks);
    }
  } catch (error) {
    console.error('Error updating daily tasks completion:', error);
  }
};

// 生成默认的每日任务
const generateDefaultDailyTasks = () => {
  const defaultTasks = [
    { 
      id: 1, 
      type: STUDY_PAGE_TYPES.KNOWLEDGE_CARD, 
      title: '经济学基础概念', 
      code: 'econ101',
      completed: false 
    },
    { 
      id: 2, 
      type: STUDY_PAGE_TYPES.EXAM_PRACTICE, 
      title: '2025上-冲刺模拟-4', 
      collection: 'teacher_exam',
      examData: '2025-上-冲刺模拟-4',
      completed: false 
    },
    { 
      id: 3, 
      type: STUDY_PAGE_TYPES.WRONG_QUESTIONS, 
      title: '错题复习', 
      completed: false 
    }
  ];
  
  localStorage.setItem(DAILY_TASKS_KEY, JSON.stringify(defaultTasks));
  console.log('Generated default daily tasks');
  return defaultTasks;
};

// 获取用户选择的专业
const getUserProfession = () => {
  try {
    const savedProfession = localStorage.getItem(USER_PROFESSION_KEY);
    if (savedProfession) {
      return JSON.parse(savedProfession);
    }
    // 默认返回教师资格
    return PROFESSIONS.TEACHER;
  } catch (error) {
    console.error('Error getting user profession:', error);
    return PROFESSIONS.TEACHER;
  }
};

// 保存用户选择的专业
const saveUserProfession = (profession) => {
  try {
    localStorage.setItem(USER_PROFESSION_KEY, JSON.stringify(profession));
    console.log('Saved user profession:', profession);
    
    // 同时更新学习目标
    saveLearningGoals(profession.goals);
    
    // 触发自定义事件，通知其他组件专业已更改
    const event = new CustomEvent('professionChanged', { detail: profession });
    window.dispatchEvent(event);
  } catch (error) {
    console.error('Error saving user profession:', error);
  }
};

// 获取学习目标
const getLearningGoals = () => {
  try {
    const savedGoals = localStorage.getItem(LEARNING_GOALS_KEY);
    if (savedGoals) {
      return JSON.parse(savedGoals);
    }
    
    // 如果没有保存的目标，使用当前专业的默认目标
    const profession = getUserProfession();
    return profession.goals;
  } catch (error) {
    console.error('Error getting learning goals:', error);
    return {
      knowledgeCards: 30,
      examQuestions: 200,
      wrongQuestions: 20
    };
  }
};

// 保存学习目标
const saveLearningGoals = (goals) => {
  try {
    localStorage.setItem(LEARNING_GOALS_KEY, JSON.stringify(goals));
    console.log('Saved learning goals:', goals);
  } catch (error) {
    console.error('Error saving learning goals:', error);
  }
};

// 计算学习进度
const calculateLearningProgress = () => {
  try {
    // 获取学习记录
    const records = JSON.parse(localStorage.getItem(USER_LEARNING_RECORDS_KEY) || '[]');
    
    // 获取学习目标
    const goals = getLearningGoals();
    
    // 如果没有记录，返回默认进度
    if (records.length === 0) {
      return {
        knowledgeCards: { completed: 0, total: goals.knowledgeCards, percentage: 0 },
        examQuestions: { completed: 0, total: goals.examQuestions, percentage: 0 },
        wrongQuestions: { reviewed: 0, total: goals.wrongQuestions, percentage: 0 },
        totalPercentage: 0
      };
    }
    
    // 统计各类型学习记录
    const today = new Date().toISOString().split('T')[0]; // 获取今天的日期部分
    
    // 过滤出今天的记录
    const todayRecords = records.filter(record => 
      record.timestamp.startsWith(today)
    );
    
    // 统计各类型的记录数量
    const knowledgeCardRecords = new Set(
      records
        .filter(r => r.type === STUDY_PAGE_TYPES.KNOWLEDGE_CARD)
        .map(r => r.params.code || '')
    ).size;
    
    const examQuestionRecords = new Set(
      records
        .filter(r => r.type === STUDY_PAGE_TYPES.EXAM_PRACTICE || r.type === STUDY_PAGE_TYPES.QUESTION_BANK)
        .map(r => r.path)
    ).size;
    
    const wrongQuestionRecords = new Set(
      records
        .filter(r => r.type === STUDY_PAGE_TYPES.WRONG_QUESTIONS)
        .map(r => r.path)
    ).size;
    
    // 计算完成百分比 - 使用动态目标值
    const knowledgeCardPercentage = Math.min(Math.round((knowledgeCardRecords / goals.knowledgeCards) * 100), 100);
    const examQuestionPercentage = Math.min(Math.round((examQuestionRecords / goals.examQuestions) * 100), 100);
    const wrongQuestionPercentage = Math.min(Math.round((wrongQuestionRecords / goals.wrongQuestions) * 100), 100);
    
    // 计算总体完成率（简单平均）
    const totalPercentage = Math.round(
      (knowledgeCardPercentage + examQuestionPercentage + wrongQuestionPercentage) / 3
    );
    
    return {
      knowledgeCards: {
        completed: knowledgeCardRecords,
        total: goals.knowledgeCards,
        percentage: knowledgeCardPercentage
      },
      examQuestions: {
        completed: examQuestionRecords,
        total: goals.examQuestions,
        percentage: examQuestionPercentage
      },
      wrongQuestions: {
        reviewed: wrongQuestionRecords,
        total: goals.wrongQuestions,
        percentage: wrongQuestionPercentage
      },
      totalPercentage
    };
  } catch (error) {
    console.error('Error calculating learning progress:', error);
    const goals = getLearningGoals();
    return {
      knowledgeCards: { completed: 0, total: goals.knowledgeCards, percentage: 0 },
      examQuestions: { completed: 0, total: goals.examQuestions, percentage: 0 },
      wrongQuestions: { reviewed: 0, total: goals.wrongQuestions, percentage: 0 },
      totalPercentage: 0
    };
  }
};

// 获取今日任务完成情况
const getDailyTasksCompletion = () => {
  try {
    // 获取今日任务
    let dailyTasks = JSON.parse(localStorage.getItem(DAILY_TASKS_KEY) || '[]');
    
    // 如果没有今日任务，生成默认任务
    if (dailyTasks.length === 0) {
      dailyTasks = generateDefaultDailyTasks();
    }
    
    // 计算完成数量和完成率
    const completedTasks = dailyTasks.filter(task => task.completed).length;
    const totalTasks = dailyTasks.length;
    const completionRate = Math.round((completedTasks / totalTasks) * 100);
    
    return {
      completedTodayTasks: completedTasks,
      todayTasks: totalTasks,
      completionRate,
      recommendedTasks: dailyTasks
    };
  } catch (error) {
    console.error('Error getting daily tasks completion:', error);
    return {
      completedTodayTasks: 0,
      todayTasks: 3,
      completionRate: 0,
      recommendedTasks: []
    };
  }
};

// 全局函数：检测并记录页面访问
// 这个函数可以在任何组件中调用
const detectAndRecordPageVisit = (path) => {
  // 检测并记录知识卡页面访问
  if (path.startsWith('/study/book/')) {
    const code = path.split('/').pop();
    recordStudyPageVisit(
      STUDY_PAGE_TYPES.KNOWLEDGE_CARD,
      path,
      '知识卡学习',
      { code }
    );
    return true;
  }
  // 检测并记录题库页面访问
  else if (path.startsWith('/tiku/')) {
    const pathParts = path.split('/');
    if (pathParts.length >= 3) {
      const category = pathParts[2];
      const subCategory = pathParts[3] || '';
      recordStudyPageVisit(
        STUDY_PAGE_TYPES.QUESTION_BANK,
        path,
        '题库练习',
        { category, subCategory }
      );
      return true;
    }
  }
  // 检测并记录考试练习页面访问
  else if (path.startsWith('/exam/practice/')) {
    const pathParts = path.split('/');
    if (pathParts.length >= 4) {
      const category = pathParts[3];
      const examData = pathParts.length >= 5 ? pathParts[4] : '';
      recordStudyPageVisit(
        STUDY_PAGE_TYPES.EXAM_PRACTICE,
        path,
        '考试练习',
        { category, examData }
      );
      console.log('Recorded exam practice visit:', path); // 调试日志
      return true;
    }
  }
  // 检测并记录错题本页面访问
  else if (path.startsWith('/wrong-questions')) {
    const id = path.split('/').length > 2 ? path.split('/').pop() : '';
    recordStudyPageVisit(
      STUDY_PAGE_TYPES.WRONG_QUESTIONS,
      path,
      '错题复习',
      { id }
    );
    return true;
  }
  return false;
};

// 添加一个全局的路由监听器
// 这段代码会在应用启动时执行一次
if (typeof window !== 'undefined') {
  // 确保在浏览器环境中运行
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  // 重写 pushState 方法
  window.history.pushState = function(state, title, url) {
    // 保存原始状态对象，包含可能的路由参数
    if (state && Object.keys(state).length > 0) {
      try {
        const path = new URL(url, window.location.origin).pathname;
        const routeStates = JSON.parse(localStorage.getItem('route_states') || '{}');
        routeStates[path] = state;
        localStorage.setItem('route_states', JSON.stringify(routeStates));
        console.log('Saved route state for:', path, state);
      } catch (error) {
        console.error('Error saving route state:', error);
      }
    }
    
    originalPushState.call(this, state, title, url);
    
    // 检测并记录页面访问
    if (url) {
      const path = new URL(url, window.location.origin).pathname;
      detectAndRecordPageVisit(path);
    }
  };

  // 重写 replaceState 方法
  window.history.replaceState = function(state, title, url) {
    originalReplaceState.call(this, state, title, url);
    
    // 检测并记录页面访问
    if (url) {
      const path = new URL(url, window.location.origin).pathname;
      detectAndRecordPageVisit(path);
    }
  };

  // 监听 popstate 事件（浏览器前进/后退按钮）
  window.addEventListener('popstate', () => {
    detectAndRecordPageVisit(window.location.pathname);
  });
}

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 添加登录状态检测
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // 添加专业选择状态
  const [profession, setProfession] = useState(getUserProfession());
  
  // 添加当前选择的专业分类状态
  const [defaultCategory, setDefaultCategory] = useState(null);
  
  // 修改卡片的通用样式
  const cardStyle = {
    padding: '24px',  // 统一内边距
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '12px',
    border: '1px solid #303030',
    height: '100%'
  };

  // 修改标题样式
  const titleStyle = {
    color: '#ffffff', 
    marginBottom: '8px',  // 减小标题和描述之间的间距
    fontSize: '18px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center'
  };

  // 修改描述文本样式
  const descStyle = {
    color: 'rgba(255, 255, 255, 0.45)', 
    fontSize: '14px',
    lineHeight: '1.5',
    margin: 0  // 移除底部边距，让容器控制间距
  };

  // 添加任务数据（实际项目中应从API获取）
  const [taskData, setTaskData] = useState({
    nextTask: "经济学基础概念",
    nextTaskCode: "econ101",  // 添加知识卡编码
    completedTodayTasks: 0,
    todayTasks: 3,
    completionRate: 0,
    category: "economics",    // 添加分类信息
    subCategory: "basics",     // 添加子分类信息
    // 添加总体学习进度数据
    overallProgress: {
      knowledgeCards: {
        completed: 0,
        total: 30,
        percentage: 0
      },
      examQuestions: {
        completed: 0,
        total: 200,
        percentage: 0
      },
      wrongQuestions: {
        reviewed: 0,
        total: 20,
        percentage: 0
      },
      // 总体完成率（加权平均）
      totalPercentage: 0
    },
    // 今日推荐任务
    recommendedTasks: []
  });

  // 添加上次学习页面状态
  const [lastStudyPage, setLastStudyPage] = useState(null);

  // 添加错题数据（实际项目中应从API获取）
  const errorData = {
    trend: [-2, -1, -3, -2, -1, 0, -1], // 最近7天的错题减少趋势
  };

  // 添加统计数据加载状态
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  // 用户统计数据
  const [statistics, setStatistics] = useState({
    weeklyQuestions: 0,
    knowledgePoints: 0,
    accuracy: 0,
    studyDuration: 0,
    streak: 0,
    accuracyDetails: {
      average: 0,
      recentExams: [],
      trend: 'stable',
      totalExams: 0
    }
  });

  // 1. First, we need to add a state to store the wrong questions count
  const [wrongQuestionsCount, setWrongQuestionsCount] = useState(0); // 初始值设为0，表示还未获取
  const [isLoadingWrongCount, setIsLoadingWrongCount] = useState(true); // 添加加载状态

  // 2. Add a function to fetch the wrong questions count from the API
  const fetchWrongQuestionsCount = async () => {
    console.log('%c开始获取错题数量...', 'color: #ff4d4f; font-weight: bold');
    
    setIsLoadingWrongCount(true); // 开始加载
    
    try {
      // 尝试从localStorage获取用户信息，可能包含错题数量
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo && userInfo.stats && userInfo.stats.wrongQuestions !== undefined) {
            console.log('%c从localStorage获取到错题数量:', 'color: #ff4d4f; font-weight: bold', userInfo.stats.wrongQuestions);
            setWrongQuestionsCount(userInfo.stats.wrongQuestions);
            // 继续尝试从API获取最新数据，但不阻塞UI显示
            fetchFromAPI(false);
            return;
          }
        } catch (e) {
          console.error('解析localStorage中的userInfo失败:', e);
        }
      }
      
      // 如果localStorage中没有数据，尝试从API获取
      await fetchFromAPI(true);
      
    } catch (error) {
      console.error('获取错题数量失败:', error);
      setWrongQuestionsCount(0);
    } finally {
      setIsLoadingWrongCount(false); // 结束加载状态
    }
  };
  
  // 从API获取错题数量的辅助函数
  const fetchFromAPI = async (updateLoadingState = true) => {
    try {
      // 尝试直接从用户信息API获取
      const userResponse = await api.get('/user/info', {
        // 不使用默认的withCredentials，而是显式设置认证头
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('用户信息API响应:', userResponse.data);
      
      if (userResponse.data && userResponse.data.user) {
        const userData = userResponse.data.user;
        
        // 保存用户信息到localStorage以便后续使用
        localStorage.setItem('userInfo', JSON.stringify(userData));
        
        // 直接使用用户信息中的wrongQuestions字段
        if (userData.stats && userData.stats.wrongQuestions !== undefined) {
          const count = userData.stats.wrongQuestions || 0;
          console.log('%c错题数量:', 'color: #ff4d4f; font-weight: bold', count);
          setWrongQuestionsCount(count);
          return true;
        }
      }
      
      // 如果用户信息API没有返回错题数量，尝试直接获取错题列表并计数
      console.log('%c尝试从错题列表API获取数量...', 'color: #ff4d4f; font-weight: bold');
      const wrongQuestionsResponse = await api.get('/wrong-questions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (wrongQuestionsResponse.data && Array.isArray(wrongQuestionsResponse.data.questions)) {
        const count = wrongQuestionsResponse.data.questions.length;
        console.log('%c从错题列表获取到错题数量:', 'color: #ff4d4f; font-weight: bold', count);
        setWrongQuestionsCount(count);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('API获取错题数量失败:', error);
      
      // 如果是401错误，尝试刷新token后重试
      if (error.response && error.response.status === 401) {
        console.log('%c尝试刷新认证后重新获取...', 'color: #ff4d4f; font-weight: bold');
        
        try {
          // 这里可以添加刷新token的逻辑，如果有的话
          // const refreshResponse = await api.post('/auth/refresh');
          // if (refreshResponse.data && refreshResponse.data.token) {
          //   localStorage.setItem('token', refreshResponse.data.token);
          //   return await fetchFromAPI(updateLoadingState);
          // }
        } catch (refreshError) {
          console.error('刷新认证失败:', refreshError);
        }
      }
      
      // 如果所有方法都失败，尝试从控制台日志中看到的数据
      // 注意：这是一个临时解决方案，应该在API修复后移除
      console.log('%c所有API方法失败，使用默认值0', 'color: #ff4d4f; font-weight: bold');
      setWrongQuestionsCount(0);
      return false;
    } finally {
      if (updateLoadingState) {
        setIsLoadingWrongCount(false);
      }
    }
  };

  // 3. Call this function in useEffect when the component mounts
  useEffect(() => {
    console.log('%cHome组件挂载，开始获取错题数量...', 'color: #ff4d4f; font-weight: bold');
    
    // 立即尝试获取错题数量
    fetchWrongQuestionsCount();
  }, []);

  // 添加一个新的useEffect来监听错题数量的变化
  useEffect(() => {
    console.log('%c错题数量已更新:', 'color: #ff4d4f; font-weight: bold', wrongQuestionsCount);
  }, [wrongQuestionsCount]);

  // 4. Add a handler for the "开始复习错题" button
  const handleReviewWrongQuestions = () => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token');
    if (!token) {
      message.warning('请先登录后再查看错题本');
      navigate('/login');
      return;
    }
    navigate('/wrong-questions');
  };

  // 从本地存储加载上次学习页面和学习进度
  useEffect(() => {
    try {
      const savedPage = localStorage.getItem(LAST_STUDY_PAGE_KEY);
      if (savedPage) {
        const parsedData = JSON.parse(savedPage);
        console.log('Loaded last study page:', parsedData); // 调试日志
        setLastStudyPage(parsedData);
      }
      
      // 加载用户专业设置
      const savedProfession = getUserProfession();
      setProfession(savedProfession);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
    
    // 每次Home组件加载时，检查当前路径是否需要记录
    // 这确保了直接访问学习页面的情况也能被记录
    detectAndRecordPageVisit(location.pathname);
    
    // 获取任务数据和学习进度
    fetchTaskData();
  }, [location.pathname]);

  // 从本地存储获取任务数据和学习进度
  const fetchTaskData = async () => {
    try {
      // 实际项目中，这里应该调用API获取数据
      // const response = await api.get('/tasks/daily');
      // if (response.data.success) {
      //   setTaskData(response.data.tasks);
      // }
      
      // 模拟API调用延迟
      const timer = setTimeout(() => {
        // 计算学习进度
        const progress = calculateLearningProgress();
        
        // 获取今日任务完成情况
        const dailyTasksCompletion = getDailyTasksCompletion();
        
        // 更新任务数据
        setTaskData(prevData => ({
          ...prevData,
          ...dailyTasksCompletion,
          overallProgress: progress
        }));
        
        console.log('Updated task data:', progress, dailyTasksCompletion);
      }, 500);
      
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('获取任务数据失败:', error);
      message.error('获取任务数据失败，请稍后重试');
    }
  };

  // 处理继续学习按钮点击
  const handleContinueStudy = () => {
    // 如果有上次学习记录，则导航到该页面
    if (lastStudyPage) {
      console.log('Navigating to last study page:', lastStudyPage.path); // 调试日志
      
      // 获取保存的路由状态，如果有的话
      try {
        const routeStates = JSON.parse(localStorage.getItem('route_states') || '{}');
        const savedState = routeStates[lastStudyPage.path];
        
        if (savedState) {
          // 使用保存的状态进行导航
          navigate(lastStudyPage.path, { 
            state: savedState,
            replace: false
          });
          console.log('Navigating with saved state:', savedState);
        } else {
          // 没有保存的状态，使用路径中的参数
          navigate(lastStudyPage.path);
        }
      } catch (error) {
        console.error('Error retrieving route state:', error);
        navigate(lastStudyPage.path);
      }
      
      return;
    }
    
    // 如果没有上次学习记录，则使用默认导航逻辑
    if (taskData.nextTaskCode) {
      // 导航到知识卡详情页
      navigate(`/study/book/${taskData.nextTaskCode}`);
    } else if (taskData.category && taskData.subCategory) {
      // 如果没有具体任务但有分类信息，导航到题库分类页
      navigate(`/tiku/${taskData.category}/${taskData.subCategory}`);
    } else {
      // 默认导航到学习中心
      navigate('/study');
    }
  };

  // 获取上次学习页面的描述文本
  const getLastStudyPageDescription = () => {
    if (!lastStudyPage) return '继续学习';
    
    const timeAgo = getTimeAgo(new Date(lastStudyPage.timestamp));
    return `继续${lastStudyPage.title}（${timeAgo}）`;
  };

  // 计算时间差的辅助函数
  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}天前`;
    } else if (diffHours > 0) {
      return `${diffHours}小时前`;
    } else if (diffMins > 0) {
      return `${diffMins}分钟前`;
    } else {
      return '刚刚';
    }
  };

  // 处理任务点击
  const handleTaskClick = (task) => {
    switch(task.type) {
      case 'knowledge_card':
        navigate(`/study/book/${task.code}`);
        break;
      case 'exam_practice':
        navigate(`/exam/practice/${task.collection}/${task.examData}`);
        break;
      case 'wrong_questions':
        navigate('/wrong-questions');
        break;
      default:
        console.warn('未知的任务类型:', task.type);
    }
  };

  // 渲染任务项
  const renderTaskItem = (task, index) => {
    const iconMap = {
      'knowledge_card': <BookOutlined style={{ color: '#1ED6FF' }} />,
      'exam_practice': <PlayCircleOutlined style={{ color: '#52c41a' }} />,
      'wrong_questions': <WarningOutlined style={{ color: '#ff7875' }} />
    };
    
    return (
      <div 
        key={task.id} 
        className="task-item"
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: index < taskData.recommendedTasks.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          cursor: 'pointer'
        }}
        onClick={() => handleTaskClick(task)}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {iconMap[task.type]}
          <Text 
            style={{ 
              marginLeft: '8px', 
              color: 'rgba(255, 255, 255, 0.85)',
              textDecoration: task.completed ? 'line-through' : 'none'
            }}
          >
            {task.title}
          </Text>
        </div>
        {task.completed ? (
          <CheckOutlined style={{ color: '#52c41a' }} />
        ) : (
          <RightOutlined style={{ color: 'rgba(255, 255, 255, 0.45)' }} />
        )}
      </div>
    );
  };

  // 处理专业选择
  const handleProfessionChange = (newProfession) => {
    setProfession(newProfession);
    saveUserProfession(newProfession);
    
    // 重新获取任务数据和学习进度
    fetchTaskData();
    
    // 提示用户已更新专业
    message.success(`已切换到${newProfession.name}专业`);
  };

  // 添加获取用户统计数据的函数
  const fetchUserStatistics = async () => {
    try {
      setStatisticsLoading(true);
      const response = await api.get('/user/info');
      
      if (response.data && response.data.success) {
        const userData = response.data.user;
        console.log('User info response:', userData);
        
        // 从用户信息中提取统计数据
        if (userData && userData.stats) {
          const statsData = userData.stats;
          console.log('从用户信息中提取的统计数据:', statsData);
          
          // 更新统计数据
          setStatistics({
            weeklyQuestions: statsData.questionsDone || 0,
            knowledgePoints: statsData.correctRate ? Math.round(statsData.correctRate * 100) : 0,
            accuracy: statsData.correctRate || 0,
            studyDuration: statsData.totalStudyTime || 0,
            streak: statsData.streak || 0,
            // 添加正确率详细信息
            accuracyDetails: {
              average: statsData.correctRate || 0,
              recentExams: [],
              trend: 'stable',
              totalExams: 0
            }
          });
          
          // 调试日志，查看获取到的学习时长（分钟）
          console.log('获取到的学习时长(分钟):', statsData.totalStudyTime);
        }
      } else {
        console.error('Failed to fetch user statistics:', response.data);
        // 不显示错误消息，使用默认数据
      }
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      
      // 检查是否是401未授权错误
      if (error.response && error.response.status === 401) {
        console.log('用户未登录或登录已过期');
        // 使用默认数据，不显示错误消息
      } else {
        // 其他错误不显示消息，使用默认数据
        console.error('获取统计数据失败:', error.message || '未知错误');
      }
    } finally {
      setStatisticsLoading(false);
    }
  };

  // 在组件加载时获取用户统计数据，添加错误处理
  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserStatistics();
    } else {
      console.log('用户未登录，使用默认统计数据');
      setStatisticsLoading(false);
    }
  }, []);

  // 修改登录提示函数，使用全局弹窗而不是导航
  const handleLoginPrompt = () => {
    // 不再导航到登录页面
    // navigate('/login');
    
    // 检查全局登录函数是否存在
    if (window.showLoginModal && typeof window.showLoginModal === 'function') {
      // 调用全局登录弹窗
      window.showLoginModal();
    } else {
      // 如果全局登录函数不存在，只显示提示消息
      message.warning('请先登录后再使用此功能');
      
      // 尝试触发自定义事件，通知需要登录
      try {
        const loginEvent = new CustomEvent('needLogin', {
          detail: { source: 'home-feature-card' }
        });
        window.dispatchEvent(loginEvent);
        console.log('已触发全局登录事件');
      } catch (error) {
        console.error('触发登录事件失败:', error);
      }
    }
  };

  // 在组件加载时检查登录状态
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // 加载当前选择的专业分类
    const lastSelected = localStorage.getItem('lastSelectedCategory');
    if (lastSelected) {
      try {
        setDefaultCategory(JSON.parse(lastSelected));
      } catch (error) {
        console.error('解析默认分类失败', error);
      }
    }
    
    // 监听分类变更事件
    const handleCategoryChange = (event) => {
      setDefaultCategory(event.detail);
    };
    
    window.addEventListener('categoryChanged', handleCategoryChange);
    
    // 清理函数
    return () => {
      window.removeEventListener('categoryChanged', handleCategoryChange);
    };
  }, []);

  return (
    <div className={styles.container}>
      <Title 
        level={2} 
        className={styles.mainTitle}
      >
        考证速通 <span className={styles.gradientText}> 解锁职考通过率</span>
      </Title>
      
      {/* 添加 SVG 图形 */}
      <div style={{ marginBottom: 48 }}>
        <svg width="1100" height="300" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="darkBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#141414', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="cardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#1e1e1e', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#242424', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#00b894', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#00cec9', stopOpacity: 1 }} />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur"/>
              <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0   0 0 0 0 0.8   0 0 0 0 1  0 0 0 0.6 0"/>
            </filter>
          </defs>

          <rect width="1100" height="300" fill="url(#darkBgGradient)"/>
          
          <path d="M0 75 h1200 M0 150 h1200 M0 225 h1200" 
                stroke="#ffffff" 
                strokeWidth="0.2" 
                strokeDasharray="2,10"
                opacity="0.1"/>
          
          <g transform="translate(80, 60)">
            <rect x="140" y="20" width="120" height="160" 
                  fill="url(#cardGradient)" 
                  stroke="#0984e3" 
                  strokeWidth="1.5" 
                  rx="10" 
                  opacity="0.6"/>
            <rect x="70" y="10" width="120" height="160" 
                  fill="url(#cardGradient)" 
                  stroke="#00b894" 
                  strokeWidth="1.5" 
                  rx="10" 
                  opacity="0.8"/>
            <rect x="0" y="0" width="120" height="160" 
                  fill="url(#cardGradient)" 
                  stroke="#00cec9" 
                  strokeWidth="1.5" 
                  rx="10" 
                  filter="drop-shadow(0 4px 6px rgba(0,0,0,0.3))"/>
            <text x="60" y="40" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="500">核心概念</text>
            <line x1="20" y1="60" x2="100" y2="60" stroke="#333333"/>
            <text x="60" y="90" textAnchor="middle" fill="#a0a0a0" fontSize="12">记忆要点</text>
            <circle cx="60" cy="120" r="15" fill="#1e1e1e" stroke="#00cec9" strokeWidth="2"/>
            <text x="60" y="125" textAnchor="middle" fill="#00cec9" fontSize="14">✓</text>
          </g>

          <path d="M 350 200 Q 500 80, 650 140 T 850 160 T 1050 150" 
                fill="none" 
                stroke="url(#progressGradient)" 
                strokeWidth="3"
                filter="drop-shadow(0 2px 4px rgba(0,206,201,0.3))"/>
          
          <path d="M 350 180 Q 500 60, 650 120 T 850 140 T 1050 130" 
                fill="none" 
                stroke="#0984e3" 
                strokeWidth="2"
                strokeOpacity="0.3"
                strokeDasharray="6,6"/>

          <g transform="translate(950, 150)">
            <circle r="50" 
                    fill="none" 
                    stroke="#333333" 
                    strokeWidth="8"/>
            <circle r="50" 
                    fill="none" 
                    stroke="url(#progressGradient)" 
                    strokeWidth="8"
                    strokeDasharray="235.5 314"
                    filter="drop-shadow(0 0 10px rgba(0,206,201,0.5))"/>
            <text x="0" y="8" 
                  textAnchor="middle" 
                  fill="#ffffff" 
                  fontSize="24"
                  fontWeight="600">75%</text>
          </g>

          <g>
            <circle cx="500" cy="80" r="6" fill="#00b894" opacity="0.8" filter="url(#glow)"/>
            <circle cx="650" cy="140" r="6" fill="#00cec9" opacity="0.8" filter="url(#glow)"/>
            <circle cx="850" cy="160" r="6" fill="#0984e3" opacity="0.8" filter="url(#glow)"/>
          </g>

          <g>
            <circle cx="400" cy="100" r="1" fill="#ffffff" opacity="0.5"/>
            <circle cx="700" cy="90" r="1" fill="#ffffff" opacity="0.5"/>
            <circle cx="900" cy="70" r="1" fill="#ffffff" opacity="0.5"/>
            <circle cx="600" cy="200" r="1" fill="#ffffff" opacity="0.5"/>
          </g>


          
          <text x="600" y="250" 
                textAnchor="middle" 
                fill="#a0a0a0" 
                fontSize="16"
                letterSpacing="2">
            基于认知科学和记忆曲线的智能学习体系
          </text>
        </svg>
      </div>

      <Row gutter={[20, 20]}>
        {/* 智能任务处理卡片 */}
        <Col xs={24} sm={12} lg={8}>
          <Card 
            className={styles.statsCard} 
            bordered={false}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: '24px' }}>
              <div className={styles.cardHeader}>
                <ThunderboltOutlined className={styles.cardHeaderIcon} style={{ color: '#1ED6FF' }} />
                <Text className={styles.cardHeaderTitle}>智能任务</Text>
              </div>
              
              <div className={styles.taskProgress}>
                <div className={styles.taskProgressHeader}>
                  <Text className={styles.taskProgressText}>今日任务:</Text>
                  <Text className={styles.taskProgressValue}>{taskData.completedTodayTasks}/{taskData.todayTasks}</Text>
                </div>
                
                <Progress 
                  percent={taskData.completionRate} 
                  showInfo={false} 
                  strokeColor={{
                    '0%': '#1ED6FF',
                    '100%': '#52c41a',
                  }}
                  trailColor="rgba(255, 255, 255, 0.08)"
                  style={{ marginBottom: '16px' }}
                />
                
                <div className={styles.taskProgressHeader}>
                  <Text className={styles.taskProgressText}>总体进度:</Text>
                  <Text className={styles.taskProgressValue}>{taskData.overallProgress.totalPercentage}%</Text>
                </div>
                
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressBarFill} 
                    className={styles.progressBarYellow}
                    style={{ width: `${taskData.overallProgress.totalPercentage}%` }} 
                  />
                </div>
                
                <div className={styles.progressStats}>
                  <div>
                    知识卡: {taskData.overallProgress.knowledgeCards.percentage}%
                  </div>
                  <div>
                    试题: {taskData.overallProgress.examQuestions.percentage}%
                  </div>
                  <div>
                    错题: {taskData.overallProgress.wrongQuestions.percentage}%
                  </div>
                </div>
              </div>
              
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />} 
                className={styles.gradientButton}
                onClick={handleContinueStudy}
              >
                {getLastStudyPageDescription()}
              </Button>
            </div>
          </Card>
          
          {/* 添加未登录遮罩 */}
          {!isLoggedIn && (
            <div 
              className={styles.loginMask}
              onClick={handleLoginPrompt}
            >
              <LockOutlined className={styles.loginMaskIcon} style={{ color: '#1ED6FF' }} />
              <Text className={styles.loginMaskText}>请登录</Text>
            </div>
          )}
        </Col>

        {/* 错题本卡片 - 修改为今日推荐 */}
        <Col xs={24} sm={12} lg={8}>
          <Card 
            className={styles.statsCard} 
            bordered={false}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: '24px' }}>
              <div className={styles.cardHeader}>
                <CalendarOutlined className={styles.cardHeaderIcon} style={{ color: '#1ED6FF' }} />
                <Text className={styles.cardHeaderTitle}>今日推荐</Text>
              </div>
              
              {/* 添加今日推荐部分 */}
              <div style={{ marginTop: '24px' }}>
                <div className={`${styles.taskItem} ${styles.taskItemBorder}`}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ color: '#1ED6FF', marginRight: '8px', fontSize: '16px' }}>📄</div>
                    <Text className={styles.taskItemText}>
                      {defaultCategory ? defaultCategory.subCategoryName : '请先选择考试专业'}
                    </Text>
                  </div>
                  <div style={{ color: '#52c41a' }}>✓</div>
                </div>
                
                <div className={`${styles.taskItem} ${styles.taskItemBorder}`}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ color: '#52c41a', marginRight: '8px', fontSize: '16px' }}>⏵</div>
                    <Text className={styles.taskItemText}>2025_上-冲刺模拟-4</Text>
                  </div>
                  <RightOutlined className={styles.taskItemIcon} />
                </div>
                
                <div className={styles.taskItem}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ color: '#ff7875', marginRight: '8px', fontSize: '16px' }}>⚠</div>
                    <Text className={styles.taskItemText}>错题复习</Text>
                  </div>
                  <CheckOutlined className={styles.taskItemIconCompleted} />
                </div>
              </div>
              
              {/* 添加错题数量标记和"进入错题本"按钮 */}
              <div style={{ marginTop: '16px', position: 'relative' }}>
                {/* 错题数量标记 */}
                <div className={styles.errorCountBadge}>
                  {isLoadingWrongCount ? (
                    <Spin size="small" />
                  ) : (
                    <span>{wrongQuestionsCount}</span>
                  )}
                </div>
                
                {/* "进入错题本"按钮 */}
                <Button 
                  type="primary" 
                  icon={<FolderOutlined />} 
                  className={styles.greenButton}
                  onClick={() => window.location.href = '/wrong-questions'}
                >
                  进入错题本
                </Button>
              </div>
            </div>
          </Card>
          
          {/* 添加未登录遮罩 */}
          {!isLoggedIn && (
            <div 
              className={styles.loginMask}
              onClick={handleLoginPrompt}
            >
              <LockOutlined className={styles.loginMaskIcon} style={{ color: '#00bea1' }} />
              <Text className={styles.loginMaskText}>请登录</Text>
            </div>
          )}
        </Col>

        {/* 统计与报告卡片 */}
        <Col xs={24} sm={12} lg={8}>
          <Card 
            className={styles.statsCard} 
            bordered={false}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartOutlined className={styles.cardHeaderIcon} style={{ color: '#1ED6FF' }} />
                  <Text className={styles.cardHeaderTitle}>统计报告</Text>
                </div>
                <BarChartOutlined style={{ color: '#1ED6FF', fontSize: '18px' }} />
              </div>
              
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <div className={styles.statValueBlue}>
                    {taskData.todayTasks}
                  </div>
                  <div className={styles.statLabel}>
                    今日任务
                  </div>
                </div>
                
                <div className={styles.statItem}>
                  <div className={styles.statValueGreen}>
                    {isLoadingWrongCount ? <LoadingOutlined /> : wrongQuestionsCount}
                  </div>
                  <div className={styles.statLabel}>
                    错题数量
                  </div>
                </div>
                
                <div className={styles.statItem}>
                  <div className={styles.statValueYellow}>
                    {statistics.accuracy ? statistics.accuracy.toFixed(1) : '0.0'}%
                  </div>
                  <div className={styles.statLabel}>
                    平均正确率
                  </div>
                </div>
                
                <div className={styles.statItem}>
                  <div className={styles.statValueTeal}>
                    <ClockCircleOutlined className={styles.clockIcon} />
                    {statistics.studyDuration > 0 ? (statistics.studyDuration / 60).toFixed(1) + 'h' : '0.0h'}
                  </div>
                  <div className={styles.statLabel}>
                    学习时长
                  </div>
                </div>
              </div>
            </div>
            
            {/* 未登录遮罩 */}
            {!isLoggedIn && (
              <div 
                className={styles.loginMask}
                onClick={handleLoginPrompt}
              >
                <LockOutlined className={styles.loginMaskIcon} style={{ color: '#722ed1' }} />
                <Text className={styles.loginMaskText}>请登录</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Home; 